import axios from "axios";
import SearchActionTypes from "./action-types";
import { SearchAction } from "./actions";
import { ISearchParams } from "./api";
import { ISearchHeaders, parseSearchHeaders } from "./utils";
import { checksum } from "src/utils/checksum";
import { SearchResponse, AiSearchSummary } from "./types";

export interface ISearchState {
    currentResults?: Spintr.ISearchResult[];
    currentAiResults?: Spintr.ISearchResult[];
    currentAiSummary?: AiSearchSummary;
    currentHeaders?: ISearchHeaders;
    errorMessageTag?: string;
    isLoadingResults: boolean;
    isLoadingMoreResults: boolean;
    lastSuccessfulParams?: ISearchParams;
    onedriveResults?: any[];
    sharepointResults?: any[];
    isActive?: boolean;

    history: {
        [checksum: number]: {
            params: ISearchParams;
            results: Spintr.ISearchResult[];
            aiResults?: Spintr.ISearchResult[];
            aiSummary?: AiSearchSummary;
            feedbackId?: string;
            feedbackLiked?: boolean;
            feedbackMessage?: string;
            feedbackMessageSent?: boolean;
            headers?: ISearchHeaders;
            lastAccessed: Date;
        };
    };
}

const initialState: ISearchState = {
    isLoadingResults: false,
    isLoadingMoreResults: false,
    history: {},
};

export const searchReducer = (state = initialState, action: SearchAction): ISearchState => {
    switch (action.type) {
        case SearchActionTypes.LoadCachedResult:
            return {
                ...state,
                currentResults: state.history[action.checksum]?.results,
                currentAiResults: state.history[action.checksum]?.aiResults,
                currentHeaders: state.history[action.checksum]?.headers,
                lastSuccessfulParams: state.history[action.checksum]?.params,
                ...(state.history[action.checksum] ? {} : {
                    isLoadingResults: false,
                    isLoadingMoreResults: false,
                    isActive: true,
                    history: {
                        ...state.history,
                        [action.checksum]: {
                            ...state.history[action.checksum],
                            // Update last accessed time
                            lastAccessed: new Date(),
                        },
                    },
                }),
            };

        case SearchActionTypes.ClearSearchCache:
            return {
                ...state,
                history: {},
                currentResults: undefined,
                currentAiResults: undefined,
                currentHeaders: undefined,
                lastSuccessfulParams: undefined,
                errorMessageTag: undefined,
                isLoadingResults: false,
                isLoadingMoreResults: false,
                isActive: false,
            };

        case SearchActionTypes.SetAiSummary:
            return {
                ...state,
                currentAiSummary: action.summary,
                history: action.checksum && state.history[action.checksum] ? {
                    ...state.history,
                    [action.checksum]: {
                        ...state.history[action.checksum],
                        aiSummary: action.summary,
                    },
                } : state.history,
            };

        case SearchActionTypes.SetAiSearchFeedback:
            return {
                ...state,
                history: action.checksum && state.history[action.checksum] ? {
                    ...state.history,
                    [action.checksum]: {
                        ...state.history[action.checksum],
                        feedbackId: action.feedbackId,
                        feedbackLiked: action.liked,
                        feedbackMessage: action.message,
                    },
                } : state.history,
            };

        case SearchActionTypes.SetAiSearchFeedbackMessageSentAction:
            return {
                ...state,
                history: action.checksum && state.history[action.checksum] ? {
                    ...state.history,
                    [action.checksum]: {
                        ...state.history[action.checksum],
                        feedbackMessageSent: action.messageSent,
                    },
                } : state.history,
            };

        case SearchActionTypes.ClearCurrentResults:
            return {
                ...state,
                currentResults: undefined,
                currentAiResults: undefined,
                currentHeaders: undefined,
                lastSuccessfulParams: undefined,
                errorMessageTag: undefined,
                isLoadingResults: false,
                isLoadingMoreResults: false,
                isActive: false,
            };

        case SearchActionTypes.ExecuteSearchFulfilled:
            const appendResults = (action.meta?.skip ?? 0) > 0;

            let spintrResults: Spintr.ISearchResult[] = [];
            let aiResults: Spintr.ISearchResult[] | undefined;
            const response: SearchResponse = action.payload.data;

            if (typeof response === "object" && "spintrResults" in response) {
                spintrResults = response.spintrResults;
                aiResults = response.aiResults;
            } else {
                spintrResults = response;
            }

            const searchParams = action.meta;
            const code = checksum(searchParams);

            const newState = {
                ...state,
                currentAiResults: aiResults,
                currentHeaders: parseSearchHeaders(
                    action.payload.headers as { [name: string]: string },
                ),
                currentResults: appendResults
                    ? state.currentResults?.concat(spintrResults)
                    : spintrResults,
                errorMessageTag: undefined,
                isLoadingResults: false,
                isLoadingMoreResults: false,
                lastSuccessfulParams: action.meta,
            }

            if (searchParams) {
                newState.history = {
                    ...state.history,
                    [code]: {
                        params: searchParams,
                        results: spintrResults,
                        aiResults: aiResults,
                        headers: newState.currentHeaders,
                        lastAccessed: new Date(),
                    },
                }
            }

            return newState;

        case SearchActionTypes.ExecuteSearchPending:
            return {
                ...state,
                errorMessageTag: undefined,
                isActive: true,

                isLoadingResults: (!action.meta.skip || action.meta.skip === 0) ? true : state.isLoadingResults,
                isLoadingMoreResults: (action.meta.skip && action.meta.skip > 0) ? true : state.isLoadingMoreResults,
            };

        case SearchActionTypes.ExecuteSearchRejected:
            if (axios.isCancel(action.payload)) {
                // don't show error message if the request was cancelled
                // due to a new search request before the previous request finished
                return state;
            }

            return {
                ...state,
                currentResults: undefined,
                currentAiResults: undefined,
                currentHeaders: undefined,
                errorMessageTag: "TeknisktFel",
                isLoadingResults: false,
                isLoadingMoreResults: false,
            };
        
        case SearchActionTypes.SetOnedriveResults:
            return {
                ...state,
                onedriveResults: action.results,
            };

        case SearchActionTypes.SetSharepointResults:
            return {
                ...state,
                sharepointResults: action.results
            };

        default:
            return state;
    }
};
